<template>
  <main>
    <div class="container">
      <div class="main-content-box">
        <div class="row flex-row d-flex justify-content-space-between top-page-divider">
          <div class="col-12 col-md-12 col-lg-6 col-xl-6">
            <h3 class="page-title">Referral Program</h3>
          </div>
          <div class="col-12 col-md-12 col-lg-6 col-xl-6">
            <div class="page-title-box-right">
              <router-link to="/account" class="btn"><i class="fas fa-long-arrow-alt-left mr-2"></i>Back to Account</router-link>
            </div>
          </div>
        </div>

        <!-- Referral Link -->
        <div class="card mb-4">
          <h5>Your Referral Link</h5>
          <hr />
          <Loading v-if="referralStats.processing" />
          <div v-else class="input-group">
            <input type="text" class="form-control mb-0" :value="referralLink" readonly />
            <button class="btn" @click="copyReferralLink" type="button">
              <i class="fas fa-copy mr-1"></i>{{ referralCopied ? 'Copied!' : 'Copy' }}
            </button>
          </div>
        </div>

        <!-- Stats Row -->
        <div class="card mb-4">
          <div class="row text-center">
            <div class="col-4">
              <p class="text-muted mb-1">Total Referrals</p>
              <h3 style="color: #30a322">{{ referralStats.data ? referralStats.data.totalReferrals : 0 }}</h3>
            </div>
            <div class="col-4">
              <p class="text-muted mb-1">Claimed Rewards</p>
              <h3 style="color: hsl(235,86%,65%)">{{ referralStats.data ? referralStats.data.claimedRewards : 0 }}</h3>
            </div>
            <div class="col-4">
              <p class="text-muted mb-1">Unclaimed Rewards</p>
              <h3 style="color: #e1b73f">{{ referralStats.data ? referralStats.data.unclaimedRewards : 0 }}</h3>
            </div>
          </div>
        </div>

        <!-- Rewards Accordion -->
        <div class="card mb-4">
          <div class="panel-group accordion" id="rewardsAccordion" role="tablist" aria-multiselectable="true">
            <div class="panel panel-default accordion-item">
              <div class="panel-heading accordion-header" role="tab" id="headingRewards">
                <a class="accordion-button" role="button" data-bs-toggle="collapse" data-bs-target="#collapseRewards" href="#collapseRewards" aria-expanded="false" aria-controls="collapseRewards">
                  <div class="d-flex justify-content-between align-items-center w-100 pt-2 pb-5">
                    <h5 class="panel-title mb-0">Rewards ({{ unclaimedRewards.length }})</h5>
                    <i class="fas fa-chevron-down" :style="{ transition: 'transform 0.3s ease', transform: rewardsOpen ? 'rotate(180deg)' : 'rotate(0deg)' }"></i>
                  </div>
                </a>
              </div>
              <div id="collapseRewards" class="accordion-collapse collapse" role="tabpanel" aria-labelledby="headingRewards" data-bs-parent="#rewardsAccordion">
                <div class="accordion-body">
                  <div v-if="allRewards.length === 0" class="text-center py-3">
                    <p class="text-muted mb-0">No rewards yet. Refer players to start earning!</p>
                  </div>
                  <div v-else class="row row-cols-1 row-cols-md-2 g-3">
                    <div class="col" v-for="reward in allRewards" :key="reward.key">
                      <div class="card" :style="reward.claimed ? claimedCardStyle : unclaimedCardStyle">
                        <div class="d-flex align-items-center mb-1">
                          <i :class="iconForType(reward.milestone.type)" class="mr-2" :style="{ color: '#30a322' }"></i>
                          <h6 class="mb-0">{{ formatType(reward.milestone.type) }} {{ toRoman(reward.milestone.tier) }}</h6>
                        </div>
                        <p class="text-muted small mb-1">{{ reward.milestone.description }}</p>
                        <p class="small mb-1"><i class="fas fa-gift mr-1" style="color: #30a322;"></i>Reward: <strong>{{ reward.milestone.rewardDescription }}</strong></p>
                        <p class="small mb-0">From: <strong>{{ reward.playerName }}</strong></p>
                        <p v-if="reward.claimed" class="small mb-0" style="color: #30a322;">Claimed: {{ formatDate(reward.claimedAt) }}</p>
                        <p v-else class="small mb-0" style="color: #e1b73f;">Claim In-game!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- My Referrals Accordion -->
        <div class="card mb-4">
          <div class="panel-group accordion" id="referralsAccordion" role="tablist" aria-multiselectable="true">
            <div class="panel panel-default accordion-item">
              <div class="panel-heading accordion-header" role="tab" id="headingReferrals">
                <a class="accordion-button" role="button" data-bs-toggle="collapse" data-bs-target="#collapseReferrals" href="#collapseReferrals" aria-expanded="false" aria-controls="collapseReferrals">
                  <div class="d-flex justify-content-between align-items-center w-100 pt-2 pb-5">
                    <h5 class="panel-title">My Referrals ({{ referralPlayers.length }})</h5>
                    <i class="fas fa-chevron-down" :style="{ transition: 'transform 0.3s ease', transform: referralsOpen ? 'rotate(180deg)' : 'rotate(0deg)' }"></i>
                  </div>
                </a>
              </div>
              <div id="collapseReferrals" class="accordion-collapse collapse" role="tabpanel" aria-labelledby="headingReferrals" data-bs-parent="#referralsAccordion">
                <div class="accordion-body">
                  <Loading v-if="referralPlayersLoading" />
                  <div v-else-if="referralPlayers.length === 0" class="text-center py-4">
                    <i class="fas fa-user-friends fa-3x text-muted mb-3 d-block"></i>
                    <p class="text-muted mb-0">No referrals yet.</p>
                  </div>
                  <div v-else>
                    <div v-for="player in referralPlayers" :key="player.name" class="card mb-3">
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <h6 class="mb-0"><strong>{{ player.name }}</strong></h6>
                        <small class="text-muted">Last login: {{ player.lastLogin ? formatDateSimple(player.lastLogin) : 'Never' }}</small>
                      </div>
                      <div class="row g-2">
                        <div class="col-4" v-for="progress in getNextMilestones(player)" :key="progress.type">
                          <div class="card text-center p-2 mx-1 md:mx-8">
                            <i :class="iconForType(progress.type)" class="mb-1" :style="{ color: '#30a322' }"></i>
                            <small class="text-muted">{{ progress.label }} {{ toRoman(progress.tier) }}</small>
                            <p class="mb-1"><strong>{{ progress.current }} / {{ progress.target }}</strong></p>
                            <div class="progress" style="height: 6px;">
                              <div class="progress-bar" :style="{ width: progress.percent + '%', backgroundColor: '#30a322' }"></div>
                            </div>
                            <small class="text-muted">{{ progress.percent }}%</small>
                            <small v-if="progress.rewardDescription" class="d-block mt-1" style="color: #30a322;"><i class="fas fa-gift mr-1"></i>{{ progress.rewardDescription }}</small>
                            <small v-else-if="progress.completed" class="d-block mt-1" style="color: #30a322;"><i class="fas fa-check-circle mr-1"></i>All Milestones Claimed!</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </main>
</template>

<script>
import APIClient from "@/services/APIClient.js";
import { dateMixin } from "@/mixins/dateMixin.js";
import Loading from "@/components/Loading";

export default {
  components: {
    Loading,
  },
  mixins: [dateMixin],
  computed: {
    referralLink() {
      if (this.referralStats.data) {
        return `${window.location.origin}/register?ref=${this.referralStats.data.referralSlug}`;
      }
      return '';
    },
    unclaimedRewards() {
      if (!this.milestones.length || !this.referralPlayers.length) return [];
      const rewards = [];
      for (const player of this.referralPlayers) {
        const claimedIds = player.claimedMilestones.map(c => c.milestoneId);
        for (const ms of this.milestones) {
          const current = this.getStatForType(player, ms.type);
          if (current >= ms.value && !claimedIds.includes(ms.id)) {
            rewards.push({ milestone: ms, playerName: player.name, claimed: false, key: `unclaimed-${ms.id}-${player.name}` });
          }
        }
      }
      return rewards;
    },
    claimedRewards() {
      if (!this.milestones.length || !this.referralPlayers.length) return [];
      const rewards = [];
      const milestoneMap = {};
      for (const ms of this.milestones) {
        milestoneMap[ms.id] = ms;
      }
      for (const player of this.referralPlayers) {
        for (const claim of player.claimedMilestones) {
          const ms = milestoneMap[claim.milestoneId];
          if (ms) {
            rewards.push({ milestone: ms, playerName: player.name, claimed: true, claimedAt: claim.claimedAt, key: `claimed-${ms.id}-${player.name}` });
          }
        }
      }
      return rewards;
    },
    allRewards() {
      return [...this.unclaimedRewards, ...this.claimedRewards];
    },
  },
  created() {
    document.title = "Referral Program - Near-Reality";
    this.apiClient = new APIClient();
  },
  mounted() {
    this.getReferralStats();
    this.getReferralPlayers();
    this.getReferralMilestones();

    const rewardsEl = document.getElementById('collapseRewards');
    rewardsEl.addEventListener('show.bs.collapse', () => { this.rewardsOpen = true; });
    rewardsEl.addEventListener('hide.bs.collapse', () => { this.rewardsOpen = false; });

    const referralsEl = document.getElementById('collapseReferrals');
    referralsEl.addEventListener('show.bs.collapse', () => { this.referralsOpen = true; });
    referralsEl.addEventListener('hide.bs.collapse', () => { this.referralsOpen = false; });
  },
  data() {
    return {
      apiClient: null,
      referralStats: {
        processing: false,
        data: null,
      },
      referralPlayers: [],
      referralPlayersLoading: false,
      referralCopied: false,
      rewardsOpen: false,
      referralsOpen: false,
      milestones: [],
      unclaimedCardStyle: {
        backgroundColor: 'rgba(225, 183, 63, 0.08)',
        borderLeft: '3px solid #e1b73f',
      },
      claimedCardStyle: {
        backgroundColor: 'rgba(48, 163, 34, 0.08)',
        borderLeft: '3px solid #30a322',
      },
    };
  },
  methods: {
    getTrueTotalSpent(player) {
      // Prefer backend-computed spend (credit package orders + misc orders) when available.
      const val =
        player?.totalSpentTrue ??
        player?.totalSpentAccurate ??
        player?.totalSpentComputed ??
        player?.totalSpent;
      const num = Number(val);
      return Number.isFinite(num) ? Math.floor(num) : 0;
    },
    toRoman(num) {
      const numerals = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
      return numerals[num] || String(num);
    },
    formatType(type) {
      switch (type) {
        case 'VOTE': return 'Vote';
        case 'PLAY_TIME': return 'PlayTime';
        case 'SPEND': return 'Spend';
        default: return type;
      }
    },
    iconForType(type) {
      switch (type) {
        case 'VOTE': return 'fas fa-vote-yea';
        case 'PLAY_TIME': return 'fas fa-clock';
        case 'SPEND': return 'fas fa-coins';
        default: return 'fas fa-star';
      }
    },
    getStatForType(player, type) {
      switch (type) {
        case 'VOTE': return player.totalVotes;
        case 'PLAY_TIME': return Math.floor(player.secondsPlayed / 3600);
        case 'SPEND': return this.getTrueTotalSpent(player);
        default: return 0;
      }
    },
    getNextMilestones(player) {
      const types = ['VOTE', 'PLAY_TIME', 'SPEND'];
      const labels = { 'VOTE': 'Votes', 'PLAY_TIME': 'Hours Played', 'SPEND': 'Amount Spent' };
      const results = [];
      for (const type of types) {
        const typeMilestones = this.milestones
          .filter(m => m.type === type)
          .sort((a, b) => a.tier - b.tier);
        const current = this.getStatForType(player, type);
        const next = typeMilestones.find(m => current < m.value);
        if (next) {
          const percent = Math.min(100, Math.round((current / next.value) * 100));
          results.push({ type, label: labels[type], current, target: next.value, percent, rewardDescription: next.rewardDescription, tier: next.tier });
        } else if (typeMilestones.length > 0) {
          const last = typeMilestones[typeMilestones.length - 1];
          results.push({ type, label: labels[type], current, target: last.value, percent: 100, rewardDescription: null, tier: last.tier, completed: true });
        }
      }
      return results;
    },
    getReferralStats() {
      this.referralStats.processing = true;
      this.apiClient
        .getReferralStats()
        .then((response) => {
          this.referralStats.processing = false;
          if (response.status === 200 && response.data) {
            this.referralStats.data = response.data;
          }
        })
        .catch(() => {
          this.referralStats.processing = false;
        });
    },
    getReferralPlayers() {
      this.referralPlayersLoading = true;
      this.apiClient
        .getReferralPlayers()
        .then((response) => {
          this.referralPlayersLoading = false;
          if (response.status === 200 && response.data) {
            this.referralPlayers = response.data;
          }
        })
        .catch(() => {
          this.referralPlayersLoading = false;
        });

      this.apiClient
        .getReferralPlayersSpend()
        .then((response) => {
          if (response.status !== 200 || !response.data || !Array.isArray(this.referralPlayers) || this.referralPlayers.length === 0) return;

          let spendByName = {};
          const data = response.data;

          if (Array.isArray(data)) {
            for (const row of data) {
              if (row && row.name != null) spendByName[String(row.name)] = row.totalSpent;
            }
          } else if (data.players && Array.isArray(data.players)) {
            for (const row of data.players) {
              if (row && row.name != null) spendByName[String(row.name)] = row.totalSpent;
            }
          } else if (typeof data === 'object') {
            spendByName = data;
          }

          this.referralPlayers = this.referralPlayers.map((p) => {
            const key = String(p?.name ?? '');
            const spend = spendByName[key];
            if (spend === undefined || spend === null) return p;
            return { ...p, totalSpentTrue: spend };
          });
        })
        .catch(() => {});
    },
    getReferralMilestones() {
      this.apiClient
        .getReferralMilestones()
        .then((response) => {
          if (response.status === 200 && response.data) {
            this.milestones = response.data;
          }
        })
        .catch(() => {});
    },
    copyReferralLink() {
      navigator.clipboard.writeText(this.referralLink);
      this.referralCopied = true;
      setTimeout(() => {
        this.referralCopied = false;
      }, 2000);
    },
  },
};
</script>
